import { useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModalProps } from '@telegram-apps/telegram-ui';
import {
  Button,
  List,
  Modal,
  Placeholder,
  Spinner,
} from '@telegram-apps/telegram-ui';
import { ProductEditForm } from './ProductEditForm';
import { ProductGalleryPanel } from './productModal/ProductGalleryPanel';
import { ProductLinksPanel } from './productModal/ProductLinksPanel';
import { ProductMediaViewerOverlay } from './productModal/ProductMediaViewerOverlay';
import { ProductViewPanel } from './productModal/ProductViewPanel';
import { ProductShootForm } from './ProductShootForm';
import {
  useDeleteProduct,
  useDeleteProductMedia,
  useReorderProductMedia,
  useSetProductMediaPrimary,
} from '../hooks/useProductMutations';
import { useProduct } from '../hooks/useProduct';
import { useProductClientLinks } from '../hooks/useProductClientLinks';
import { useSwipeNavigation } from '../hooks/useSwipeNavigation';
import type { ProductMediaItem } from '../types/product';
import { useBackButton } from '../lib/telegram';
import { copyText, shareImageUrl } from '../lib/share';
import {
  clientTryOnMessage,
  confirmTelegram,
  fallbackMediaFromProduct,
} from './productModal/productModalUtils';

type ProductModalMode = 'view' | 'edit' | 'shoot' | 'gallery' | 'links';


export function ProductModal({
  open,
  productId,
  initialMode = 'view',
  onClose,
}: {
  open: boolean;
  productId: string | null;
  initialMode?: Extract<ProductModalMode, 'view' | 'links'>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<ProductModalMode>('view');
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const [viewerMedia, setViewerMedia] = useState<ProductMediaItem | null>(null);
  const q = useProduct(productId);
  const p = q.data;
  const linksQuery = useProductClientLinks(productId, open && mode === 'links' && p?.status === 'active');
  const del = useDeleteProduct();
  const setPrimary = useSetProductMediaPrimary();
  const reorderMedia = useReorderProductMedia();
  const deleteMedia = useDeleteProductMedia();

  const handleClose = useCallback(() => {
    setMode('view');
    onClose();
  }, [onClose]);

  useEffect(() => {
    setMode(initialMode);
    setSelectedMediaIndex(0);
    setViewerMedia(null);
  }, [initialMode, open, productId]);

  useBackButton(handleClose, open);

  const onOpenChange: NonNullable<ModalProps['onOpenChange']> = (isOpen) => {
    if (!isOpen) handleClose();
  };
  const modalTitle =
    mode === 'edit' ? 'Редактирование' : mode === 'gallery' ? 'Галерея' : mode === 'links' ? 'Отправить' : 'Товар';

  const media = useMemo(() => {
    if (!p) return [];
    return p.media?.length ? p.media : fallbackMediaFromProduct(p);
  }, [p]);
  const selectedMedia = media[selectedMediaIndex] ?? media[0] ?? null;
  const viewerMediaIndex = viewerMedia
    ? Math.max(0, media.findIndex((item) => item.id === viewerMedia.id))
    : selectedMediaIndex;
  const productSwipeHandlers = useSwipeNavigation({
    count: media.length,
    currentIndex: selectedMediaIndex,
    enabled: mode === 'view',
    onChange: setSelectedMediaIndex,
  });
  const viewerSwipeHandlers = useSwipeNavigation({
    count: media.length,
    currentIndex: viewerMediaIndex,
    enabled: !!viewerMedia,
    onChange: (index) => {
      const item = media[index];
      if (!item) return;
      setSelectedMediaIndex(index);
      setViewerMedia(item);
    },
  });

  useEffect(() => {
    if (selectedMediaIndex >= media.length) {
      setSelectedMediaIndex(0);
    }
  }, [media.length, selectedMediaIndex]);

  const handleDelete = async () => {
    if (!p) return;
    const ok = await confirmTelegram(
      'Удалить товар? Он будет скрыт из каталога (soft delete).',
    );
    if (!ok) return;
    del.mutate(p.id, { onSuccess: () => handleClose() });
  };

  const handleShare = useCallback(() => {
    const item = selectedMedia;
    if (!item) return;
    const title = p?.name ? `${p.name} · FitBot` : item.title;
    void shareImageUrl(item.url, title);
  }, [p?.name, selectedMedia]);

  const handleCopyClientLink = useCallback((url: string) => {
    void copyText(url, 'Ссылка для клиента скопирована');
  }, []);

  const handleCopyClientMessage = useCallback((url: string) => {
    const productName = p?.name ?? 'товар';
    void copyText(
      clientTryOnMessage(productName, url),
      'Сообщение клиенту скопировано',
    );
  }, [p?.name]);

  const moveMedia = (fromIndex: number, direction: -1 | 1) => {
    if (!p) return;
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= media.length) return;
    const ids = media.map((item) => item.id);
    const tmp = ids[fromIndex];
    ids[fromIndex] = ids[toIndex]!;
    ids[toIndex] = tmp!;
    reorderMedia.mutate({ productId: p.id, ids });
    setSelectedMediaIndex(toIndex);
  };

  const handleDeleteMedia = async (item: ProductMediaItem) => {
    if (!p || item.kind === 'source') return;
    const ok = await confirmTelegram('Убрать изображение из галереи товара?');
    if (!ok) return;
    deleteMedia.mutate({ productId: p.id, mediaId: item.id });
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      header={<Modal.Header>{modalTitle}</Modal.Header>}
      dismissible
    >
      {open && (
        <>
          {q.isPending && (
            <Placeholder header="Загрузка">
              <Spinner size="m" />
            </Placeholder>
          )}
          {q.isError && (
            <Placeholder
              header="Ошибка"
              description={(q.error as Error)?.message ?? 'Не удалось загрузить товар'}
              action={
                <Button mode="filled" size="s" onClick={() => void q.refetch()}>
                  Повторить
                </Button>
              }
            />
          )}
          {!q.isPending && !q.isError && p && mode === 'edit' && (
            <>
              <List style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                <ProductEditForm
                  product={p}
                  onCancel={() => setMode('view')}
                  onDone={() => {
                    void q.refetch();
                    setMode('view');
                  }}
                  onDelete={() => void handleDelete()}
                  deletePending={del.isPending}
                  deleteError={del.error instanceof Error ? del.error : null}
                />
              </List>
            </>
          )}
          {!q.isPending && !q.isError && p && mode === 'shoot' && (
            <List style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <ProductShootForm
                productId={p.id}
                onDone={(generationId) => {
                  handleClose();
                  navigate(`/generations/${generationId}`);
                }}
                onCancel={() => setMode('view')}
              />
            </List>
          )}
          {!q.isPending && !q.isError && p && mode === 'gallery' && (
            <ProductGalleryPanel
              productId={p.id}
              media={media}
              busy={setPrimary.isPending || reorderMedia.isPending || deleteMedia.isPending}
              onDone={() => setMode('view')}
              onOpenViewer={setViewerMedia}
              onSetPrimary={(input) => setPrimary.mutate(input)}
              onMoveMedia={moveMedia}
              onDeleteMedia={(item) => void handleDeleteMedia(item)}
            />
          )}
          {!q.isPending && !q.isError && p && mode === 'links' && (
            <ProductLinksPanel
              productName={p.name}
              productStatus={p.status}
              links={linksQuery.data?.links ?? null}
              loading={linksQuery.isPending}
              error={linksQuery.isError ? (linksQuery.error as Error) : null}
              onRetry={() => void linksQuery.refetch()}
              onDone={() => setMode('view')}
              onCopyClientLink={handleCopyClientLink}
              onCopyClientMessage={handleCopyClientMessage}
            />
          )}
          {!q.isPending && !q.isError && p && mode === 'view' && (
            <ProductViewPanel
              product={p}
              media={media}
              selectedMedia={selectedMedia}
              selectedMediaIndex={selectedMediaIndex}
              swipeHandlers={productSwipeHandlers}
              onSelectMediaIndex={setSelectedMediaIndex}
              onOpenViewer={setViewerMedia}
              onOpenLinks={() => setMode('links')}
              onOpenShoot={() => setMode('shoot')}
              onOpenGallery={() => setMode('gallery')}
              onOpenEdit={() => setMode('edit')}
              onShare={handleShare}
              onClose={handleClose}
            />
          )}
          {viewerMedia && (
            <ProductMediaViewerOverlay
              media={media}
              viewerMedia={viewerMedia}
              swipeHandlers={viewerSwipeHandlers}
              onClose={() => setViewerMedia(null)}
            />
          )}
        </>
      )}
    </Modal>
  );
}
